import { useState } from 'react';

// MUI
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { red } from '@mui/material/colors';

// FontAwesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faUpload, faChevronDown, faXmark } from '@fortawesome/pro-light-svg-icons';
import ElariomTryIt, { RATE_FILE_TYPES } from '../../elariom/ElariomTryIt.js';

const VALID_PRODUCTS = [
    // Sea
    "20' Dry", "20' Flat", "20' Open Top", "20' Reefer",
    "40' Dry", "40' Flat", "40' Open Top", "40' Reefer",
    "40' HC Dry", "40' HC Flat", "40' HC Open Top", "40' HC Reefer",
    "45' HC Dry", "45' HC Reefer", "LCL",
    // Air
    "Air freight",
    // Land
    "Dry", "Reefer", "Tent", "Mega",
];

const FIXED_COLUMNS = [
    'source', 'destination', 'transshipment', 'transitTime',
    'transportationMethod', 'validFrom', 'validUntil', 'form',
    'supplier', 'supplierEmail', 'product', 'dangerous', 'type', 'Carrier'
];

const TEMPLATE_HEADER = FIXED_COLUMNS.join(',') + ',Ocean Freight';

function downloadTemplate() {
    const csv = TEMPLATE_HEADER + '\n';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'monada-rates-template.csv';
    a.click();
    URL.revokeObjectURL(url);
}

function FreeUploadTemplate({ org, accesstoken, me }) {
    // A file picked with "Upload file": runs the rate-parsing workflow in a dialog.
    const [ rateFile, setRateFile ] = useState(null);
    return (
        <Paper sx={{ p: 3, mt: 3 }} elevation={0}>
            <Typography sx={{ fontSize: '16px', fontWeight: 700, mb: 0.5 }}>Upload rates</Typography>
            <Typography sx={{ fontSize: '13px', color: '#64748b', mb: 2 }}>
                Upload a rate file: PDF, Excel, CSV or an Outlook email (.eml). It is read for you; you review the rates, then publish them. A file in the Monada CSV template is read as it is.
            </Typography>

            {/* Instructions */}
            <Accordion sx={{ mb: 2, '&:before': { display: 'none' }, border: '1px solid #e2e8f0' }} elevation={0}>
                <AccordionSummary expandIcon={<FontAwesomeIcon icon={faChevronDown} />}>
                    <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>How to fill in the template</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Table size='small'>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700, fontSize: '11px' }}>Column</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '11px' }}>Required</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '11px' }}>Instructions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {[
                                { col: 'source', req: 'Yes', desc: 'UN/LOCODE for the origin port (e.g. CNNGB). If unknown, enter city name followed by country (e.g. Ningbo, China).' },
                                { col: 'destination', req: 'Yes', desc: 'UN/LOCODE for the destination port (e.g. RSBEG). Same rules as source.' },
                                { col: 'transshipment', req: 'No', desc: 'Free text. Transshipment port if applicable.' },
                                { col: 'transitTime', req: 'Yes', desc: 'Transit time in days. Numeric value only (no text or ranges). Enter -1 if unknown.' },
                                { col: 'transportationMethod', req: 'Yes', desc: 'sea or air.' },
                                { col: 'validFrom', req: 'Yes', desc: 'Start date. Use yyyy-mm-dd (e.g. 2026-08-24) — this is what Monada writes everywhere else, so it keeps every rate in one format. mm/dd/yyyy (08/24/2026) is also accepted. Do NOT use day-first (24/08/2026): slash dates are read month-first, so day-first dates are wrong or invalid.' },
                                { col: 'validUntil', req: 'Yes', desc: 'End date, same formats as validFrom. Must be on or after validFrom.' },
                                { col: 'form', req: 'Yes', desc: 'The title of the supplier rate form these rates should use — any form in this workspace, not a fixed value. It must match the form title exactly, including capitalisation. Every charge column in your file must also match a field title on that form exactly; a column the form does not have is silently dropped on import. Before uploading, open the form and check its fields are set up with the calculation you intend (per container, per shipment, per chargeable weight, W/M), because the form decides how each charge is multiplied — not the CSV.' },
                                { col: 'supplier', req: 'Yes', desc: 'Agent or carrier company name.' },
                                { col: 'supplierEmail', req: 'Yes', desc: 'Contact email for the supplier.' },
                                { col: 'product', req: 'Yes', desc: `Container type. Case-sensitive. Must be one of: ${VALID_PRODUCTS.join(', ')}` },
                                { col: 'dangerous', req: 'Yes', desc: 'TRUE or FALSE. Case-sensitive.' },
                                { col: 'type', req: 'Yes', desc: 'Must be contract. Case-sensitive — do not change.' },
                                { col: 'Carrier', req: 'No', desc: 'Shipping line name (e.g. MSC, CMA CGM).' },
                                { col: 'Charges (Ocean Freight, THC, etc.)', req: 'No', desc: 'Each charge gets its own column. Money must carry its currency, in value|CURRENCY format (e.g. 1850|USD, 520|EUR). Add as many charge columns as needed.' },
                                { col: 'Numbers that are not money (Free Time, etc.)', req: 'No', desc: 'A column like Free Time holding a plain number (e.g. 21 days) is fine — leave the currency off. Only money needs value|CURRENCY.' },
                                { col: 'Notes / Remarks', req: 'No', desc: 'Add any free text column for notes or remarks (e.g. "Subject to OWS", validity conditions). Any column that is not in value|CURRENCY format is treated as free text.' },
                            ].map(r => (
                                <TableRow key={r.col}>
                                    <TableCell sx={{ fontSize: '11px', fontWeight: 600, whiteSpace: 'nowrap' }}>{r.col}</TableCell>
                                    <TableCell sx={{ fontSize: '11px', color: r.req === 'Yes' ? red[500] : '#94a3b8' }}>{r.req}</TableCell>
                                    <TableCell sx={{ fontSize: '11px' }}>{r.desc}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </AccordionDetails>
            </Accordion>

            {/* Download + Upload */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Button variant='outlined' startIcon={<FontAwesomeIcon icon={faDownload} />} onClick={downloadTemplate} sx={{ textTransform: 'none' }}>
                    Download empty template
                </Button>
                {/* Any rate file goes through the rate-parsing workflow: read, review, publish. */}
                <Button variant='contained' component='label' startIcon={<FontAwesomeIcon icon={faUpload} />} sx={{ textTransform: 'none' }}>
                    Upload file
                    <input type='file' accept={RATE_FILE_TYPES} hidden onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) setRateFile(f); }} />
                </Button>
            </Box>

            <Dialog open={!!rateFile} onClose={() => setRateFile(null)} fullWidth maxWidth='xl'>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ flex: 1 }}>
                        Upload rates
                        <Typography sx={{ fontSize: '12px', color: '#64748b' }}>Choose the details, let the file be read, then review and publish the rates to {org.title || org.alias}.</Typography>
                    </Box>
                    <IconButton aria-label='Close' onClick={() => setRateFile(null)}><FontAwesomeIcon icon={faXmark} /></IconButton>
                </DialogTitle>
                <DialogContent>
                    {rateFile && <ElariomTryIt key={`${rateFile.name}-${rateFile.lastModified}`} org={org} accesstoken={accesstoken} startFile={rateFile} />}
                </DialogContent>
            </Dialog>
        </Paper>
    );
}

export default FreeUploadTemplate;
